import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clock,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Send,
  Search,
} from 'lucide-react';
import {
  SERVICE_HOUR_RATE_LABEL,
  SERVICE_HOUR_RATE_USD,
  adminAdjustHours,
  adminCreateEngagement,
  adminLogWork,
  adminSendPurchase,
  fetchServiceHoursSnapshot,
  formatHours,
  setServiceHourRetainer,
  type ServiceHoursSnapshot,
} from '../../lib/serviceHours';
import { supabase } from '../../lib/supabase';

type BusinessOption = { id: string; name: string };

type Props = {
  /** Preloaded businesses from Super Admin dashboard (optional). */
  businesses?: BusinessOption[];
  /** When set, skip the business picker and manage this business only. */
  lockedBusiness?: BusinessOption | null;
  /** Shown in modal header actions (optional). */
  onClose?: () => void;
};

export default function SuperAdminServiceHoursPanel({
  businesses: preload = [],
  lockedBusiness = null,
  onClose,
}: Props) {
  const [businesses, setBusinesses] = useState<BusinessOption[]>(preload);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(lockedBusiness?.id ?? null);
  const [snapshot, setSnapshot] = useState<ServiceHoursSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Quote / send
  const [quoteHours, setQuoteHours] = useState(8);
  const [quoteTitle, setQuoteTitle] = useState('Platform setup');
  const [quoteKind, setQuoteKind] = useState<'setup' | 'seo' | 'other'>('setup');
  const [quoteNote, setQuoteNote] = useState('');

  // Log work
  const [logHours, setLogHours] = useState(1);
  const [logNote, setLogNote] = useState('');
  const [logEngagementId, setLogEngagementId] = useState('');

  // Retainer / adjust
  const [retainerQty, setRetainerQty] = useState(3);
  const [adjustHours, setAdjustHours] = useState(1);
  const [adjustNote, setAdjustNote] = useState('');

  const isLocked = Boolean(lockedBusiness?.id);

  useEffect(() => {
    if (lockedBusiness?.id) {
      setSelectedId(lockedBusiness.id);
      return;
    }
    if (preload.length) {
      setBusinesses(preload);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('businesses')
        .select('id, name')
        .order('name', { ascending: true })
        .limit(500);
      if (!cancelled && data) setBusinesses(data as BusinessOption[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [preload, lockedBusiness?.id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return businesses.slice(0, 80);
    return businesses
      .filter((b) => b.name?.toLowerCase().includes(q) || b.id.toLowerCase().includes(q))
      .slice(0, 80);
  }, [businesses, search]);

  const selectedName =
    lockedBusiness?.name || businesses.find((b) => b.id === selectedId)?.name || '';

  const loadSnapshot = useCallback(async (businessId: string) => {
    setLoading(true);
    setError(null);
    try {
      const snap = await fetchServiceHoursSnapshot(businessId);
      setSnapshot(snap);
      if (snap.retainer.quantity > 0) setRetainerQty(snap.retainer.quantity);
    } catch (e) {
      setSnapshot(null);
      setError(e instanceof Error ? e.message : 'Failed to load service hours');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) void loadSnapshot(selectedId);
    else setSnapshot(null);
  }, [selectedId, loadSnapshot]);

  const run = async (fn: () => Promise<void>) => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await loadSnapshot(selectedId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
              Service hours
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {SERVICE_HOUR_RATE_LABEL} · buy whole hours · log in 0.5 hr steps · unused hours roll over
            </p>
            {isLocked && selectedName && (
              <p className="text-sm font-medium text-gray-900 mt-2 truncate">{selectedName}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedId && (
              <button
                type="button"
                onClick={() => void loadSnapshot(selectedId)}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:text-gray-800"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:text-gray-800 text-lg leading-none"
                title="Close"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {!isLocked && (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search businesses…"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-100 divide-y divide-gray-50">
              {filtered.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setSelectedId(b.id)}
                  className={`w-full text-left px-3 py-2.5 text-sm transition-colors ${
                    selectedId === b.id ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-800'
                  }`}
                >
                  <span className="font-medium">{b.name || 'Unnamed'}</span>
                  <span className="block text-[11px] text-gray-400 font-mono truncate">{b.id}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="px-3 py-4 text-sm text-gray-500">No businesses match.</p>
              )}
            </div>
          </>
        )}
      </div>

      {!selectedId ? (
        <p className="text-sm text-gray-500 px-1">Select a business to manage hours.</p>
      ) : loading && !snapshot ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-3 py-2">
              {notice}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-1">
              {selectedName}
            </p>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <p className="text-sm text-gray-500">Balance</p>
                <p className="text-3xl font-bold text-gray-900 tabular-nums">
                  {formatHours(snapshot?.balance.hours_balance ?? 0)}
                  <span className="text-base font-normal text-gray-500"> hrs</span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Retainer</p>
                <p className="text-xl font-semibold text-gray-900 tabular-nums">
                  {snapshot?.retainer.quantity ?? 0} hrs/mo
                  {(snapshot?.retainer.monthly_usd ?? 0) > 0 && (
                    <span className="text-sm font-normal text-gray-500">
                      {' '}
                      · ${snapshot?.retainer.monthly_usd}/mo
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Used / purchased</p>
                <p className="text-sm text-gray-800 tabular-nums">
                  {formatHours(snapshot?.balance.total_hours_used ?? 0)} used ·{' '}
                  {formatHours(snapshot?.balance.total_hours_purchased ?? 0)} purchased
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Send invoice */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Quote & send invoice</h3>
              <input
                value={quoteTitle}
                onChange={(e) => setQuoteTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                placeholder="Title"
              />
              <div className="flex gap-2">
                <select
                  value={quoteKind}
                  onChange={(e) => setQuoteKind(e.target.value as 'setup' | 'seo' | 'other')}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm"
                >
                  <option value="setup">Setup</option>
                  <option value="seo">SEO</option>
                  <option value="other">Other</option>
                </select>
                <div className="flex items-center gap-1 flex-1">
                  <button
                    type="button"
                    className="p-2 rounded-lg border border-gray-200"
                    onClick={() => setQuoteHours((h) => Math.max(1, h - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={quoteHours}
                    onChange={(e) => setQuoteHours(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
                    className="w-full text-center px-2 py-2 rounded-lg border border-gray-200 text-sm tabular-nums"
                  />
                  <button
                    type="button"
                    className="p-2 rounded-lg border border-gray-200"
                    onClick={() => setQuoteHours((h) => h + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Total: ${quoteHours * SERVICE_HOUR_RATE_USD} ({quoteHours} × {SERVICE_HOUR_RATE_LABEL})
              </p>
              <textarea
                value={quoteNote}
                onChange={(e) => setQuoteNote(e.target.value)}
                rows={2}
                placeholder="Note (optional)"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const res = await adminSendPurchase({
                      businessId: selectedId!,
                      hours: quoteHours,
                      title: quoteTitle,
                      kind: quoteKind,
                      note: quoteNote || undefined,
                      sendInvoice: true,
                    });
                    setNotice(
                      res.invoiceUrl
                        ? `Invoice sent. Open: ${res.invoiceUrl}`
                        : `Invoice ${res.invoiceId} created and emailed.`,
                    );
                  })
                }
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                Send Stripe invoice
              </button>
            </div>

            {/* Log work */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Log work</h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-2 rounded-lg border border-gray-200"
                  onClick={() => setLogHours((h) => Math.max(0.5, Math.round((h - 0.5) * 10) / 10))}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={logHours}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n) && n > 0) setLogHours(Math.round(n * 2) / 2);
                  }}
                  className="w-full text-center px-2 py-2 rounded-lg border border-gray-200 text-sm tabular-nums"
                />
                <button
                  type="button"
                  className="p-2 rounded-lg border border-gray-200"
                  onClick={() => setLogHours((h) => Math.round((h + 0.5) * 10) / 10)}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <select
                value={logEngagementId}
                onChange={(e) => setLogEngagementId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              >
                <option value="">No engagement</option>
                {(snapshot?.engagements || []).map((eng) => (
                  <option key={eng.id} value={eng.id}>
                    {eng.title} ({eng.status})
                  </option>
                ))}
              </select>
              <textarea
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                rows={2}
                placeholder="What did you work on?"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await adminLogWork({
                      businessId: selectedId!,
                      hours: logHours,
                      note: logNote || undefined,
                      engagementId: logEngagementId || undefined,
                    });
                    setNotice(`Logged ${formatHours(logHours)} hrs.`);
                    setLogNote('');
                  })
                }
                className="w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-50"
              >
                Debit hours
              </button>
            </div>

            {/* Retainer */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Monthly retainer</h3>
              <p className="text-xs text-gray-500">
                Adds a line item on their Nexus subscription. Unused hours carry forever.
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="p-2 rounded-lg border border-gray-200"
                  onClick={() => setRetainerQty((q) => Math.max(0, q - 1))}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={retainerQty}
                  onChange={(e) => setRetainerQty(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                  className="w-full text-center px-2 py-2 rounded-lg border border-gray-200 text-sm tabular-nums"
                />
                <button
                  type="button"
                  className="p-2 rounded-lg border border-gray-200"
                  onClick={() => setRetainerQty((q) => q + 1)}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {retainerQty === 0
                  ? 'Remove retainer'
                  : `$${retainerQty * SERVICE_HOUR_RATE_USD}/mo`}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const res = await setServiceHourRetainer({
                      businessId: selectedId!,
                      quantity: retainerQty,
                    });
                    setNotice(
                      res.quantity > 0
                        ? `Retainer set to ${res.quantity} hrs/mo ($${res.monthly_usd}/mo).`
                        : 'Retainer removed.',
                    );
                  })
                }
                className="w-full py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                Save retainer
              </button>
            </div>

            {/* Adjust */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Manual adjust</h3>
              <p className="text-xs text-gray-500">Positive credits, negative debits (0.5 steps).</p>
              <input
                type="number"
                step={0.5}
                value={adjustHours}
                onChange={(e) => setAdjustHours(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm tabular-nums"
              />
              <input
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                placeholder="Reason"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await adminCreateEngagement({
                        businessId: selectedId!,
                        title: quoteTitle || 'Engagement',
                        kind: quoteKind,
                        quotedHours: quoteHours,
                        notes: quoteNote || undefined,
                      });
                      setNotice('Engagement created (no invoice).');
                    })
                  }
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Create engagement only
                </button>
                <button
                  type="button"
                  disabled={busy || !adjustHours}
                  onClick={() =>
                    void run(async () => {
                      await adminAdjustHours({
                        businessId: selectedId!,
                        hours: adjustHours,
                        note: adjustNote || undefined,
                      });
                      setNotice(`Adjusted by ${formatHours(adjustHours)} hrs.`);
                    })
                  }
                  className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
                >
                  Apply adjust
                </button>
              </div>
            </div>
          </div>

          {/* History */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent transactions</h3>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(snapshot?.transactions || []).length === 0 && (
                <p className="text-sm text-gray-500">No transactions yet.</p>
              )}
              {(snapshot?.transactions || []).map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-start justify-between gap-3 py-2 border-b border-gray-50 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {tx.transaction_type.replace('_', ' ')}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{tx.note || '—'}</p>
                    <p className="text-[11px] text-gray-400">
                      {new Date(tx.created_at).toLocaleString()}
                    </p>
                  </div>
                  <p
                    className={`text-sm font-semibold tabular-nums shrink-0 ${
                      tx.hours >= 0 ? 'text-emerald-700' : 'text-red-700'
                    }`}
                  >
                    {tx.hours >= 0 ? '+' : ''}
                    {formatHours(tx.hours)}h
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
